import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RefreshCw, Mail, Phone, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const [quotes, setQuotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchQuotes = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/quote-requests`);
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, []);

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-NZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Quote Requests Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage and review customer quote requests
            </p>
          </div>
          <Button
            onClick={fetchQuotes}
            disabled={isLoading}
            variant="outline"
            className="rounded-none"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <Card className="rounded-none">
          <CardHeader>
            <CardTitle>All Quote Requests</CardTitle>
            <CardDescription>
              Total Requests: {quotes.length}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Configuration</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quotes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      {isLoading ? 'Loading...' : 'No quote requests yet'}
                    </TableCell>
                  </TableRow>
                ) : (
                  quotes.map((quote) => (
                    <TableRow key={quote.id}>
                      <TableCell className="font-mono text-xs">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(quote.timestamp)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{quote.customer_name}</div>
                        {quote.customer_message && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {quote.customer_message}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 text-xs">
                          <div className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            <a href={`mailto:${quote.customer_email}`} className="hover:underline">
                              {quote.customer_email}
                            </a>
                          </div>
                          {quote.customer_phone && (
                            <div className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              <a href={`tel:${quote.customer_phone}`} className="hover:underline">
                                {quote.customer_phone}
                              </a>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <Badge variant="outline" className="rounded-none font-mono text-xs">
                            {quote.configuration.profile}
                          </Badge>
                          <div className="text-xs text-muted-foreground space-y-0.5">
                            <div>{quote.configuration.door_type} · {quote.configuration.top_style}</div>
                            <div>{quote.configuration.panel_style}</div>
                            <div>{quote.configuration.width}×{quote.configuration.height}mm</div>
                            {quote.configuration.side_lites !== 'None' && (
                              <div>Side Lites: {quote.configuration.side_lites}</div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="font-bold">
                          ${quote.estimated_price.toLocaleString()}
                        </div>
                        <div className="text-xs text-muted-foreground">NZD</div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
